/**
 * WarrantyClaimChatPage — claim-scoped chat between the claimant + tech +
 * admins. Files dropped here back-link into the claim's media list.
 *
 * iter142b — Phase 2.  Includes:
 *   • Live message list with system / text / file / ai bubbles
 *   • Text composer + drag-drop / file-picker uploader
 *   • "Ask AI to evaluate close" button (staff only)
 *   • "Close case" button (gated on can_close from server)
 */
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChatCircle, PaperPlaneTilt, Paperclip, Lock, Robot, ArrowLeft, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminPageHeader from "@/components/AdminPageHeader";
import WarrantyChatTour from "@/components/WarrantyChatTour";

export default function WarrantyClaimChatPage({ user, onLogout, onLogoutAll }) {
  const { cid } = useParams();
  const nav = useNavigate();
  const [chat, setChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState("");
  const [sending, setSending] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const fileInput = useRef(null);
  const bottomRef = useRef(null);
  const isStaff = !!user?.is_admin;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/warranty/claims/${encodeURIComponent(cid)}/chat`);
      setChat(r.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load chat");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);

  // iter144c — Real-time SSE stream replaces the 8s poll. Falls back
  // to a slow 30s poll when the EventSource fails (corporate proxies
  // sometimes block long-lived streams). On every server event we
  // call load() to refetch the canonical chat doc — the SSE payload
  // is just a "something changed" hint, never the source of truth.
  useEffect(() => {
    if (!cid) return;
    if (chat?.closed) return;
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    let es;
    let pollId;
    let cancelled = false;
    try {
      es = new EventSource(`${apiBase}/api/warranty/claims/${encodeURIComponent(cid)}/chat/stream`,
                           { withCredentials: true });
      es.addEventListener("message", () => { if (!cancelled) load(); });
      es.addEventListener("closed",  () => { if (!cancelled) load(); });
      es.onerror = () => {
        // Tear down ES + downgrade to slow poll. We don't re-open
        // automatically since the user might have lost network — the
        // 30s poll is enough to stay alive until the next page action.
        try { es.close(); } catch {}
        if (!pollId && !cancelled) pollId = setInterval(load, 30000);
      };
    } catch {
      pollId = setInterval(load, 30000);
    }
    return () => {
      cancelled = true;
      try { es && es.close(); } catch {}
      if (pollId) clearInterval(pollId);
    };
    // eslint-disable-next-line
  }, [cid, chat?.closed]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [chat?.messages?.length]);

  const send = async () => {
    const body = composing.trim();
    if (!body) return;
    setSending(true);
    try {
      await api.post(`/warranty/claims/${encodeURIComponent(cid)}/chat/messages`, { body });
      setComposing("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error("File too large (max 50 MB)"); return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("note", composing.trim());
    setSending(true);
    try {
      await api.post(
        `/warranty/claims/${encodeURIComponent(cid)}/chat/files`, fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setComposing("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setSending(false); }
  };

  const askAi = async () => {
    setAiBusy(true);
    try {
      const r = await api.post(`/warranty/claims/${encodeURIComponent(cid)}/chat/ai-close-suggest`);
      if (r.data?.recommendation === "close") {
        toast.success("AI thinks this can be closed — see the new bubble.");
      } else {
        toast.info(`AI: ${r.data?.recommendation || "needs more"} — ${r.data?.rationale || ""}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI request failed");
    } finally { setAiBusy(false); }
  };

  const closeCase = async () => {
    const reason = window.prompt("Reason for closing? (saved to audit log)", "Resolved by tech");
    if (reason === null) return;
    setSending(true);
    try {
      await api.post(`/warranty/claims/${encodeURIComponent(cid)}/chat/close`, { reason });
      toast.success("Case closed.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Close failed");
    } finally { setSending(false); }
  };

  if (loading && !chat) {
    return (
      <div className="min-h-screen bg-slate-50 font-['Lato'] flex items-center justify-center">
        <p className="text-sm text-slate-500">Loading chat…</p>
      </div>
    );
  }
  if (!chat) return null;
  const closed = !!chat.closed;

  return (
    <div className="min-h-screen bg-slate-50 font-['Lato'] text-slate-900 flex flex-col" data-testid="warranty-chat-page">
      <AdminPageHeader
        sticky maxWidth="max-w-4xl" testId="warranty-chat-back"
        icon={<ChatCircle size={20} weight="fill" className="text-blue-700" />}
        title={`Warranty chat · ${chat.ticket_number || cid.slice(0, 8)}`}
        subtitle={closed ? "Case closed" : `${chat.participants?.length || 0} participant(s)`}
        user={user} onLogout={onLogout} onLogoutAll={onLogoutAll}
      />

      <main className="max-w-4xl w-full mx-auto p-4 flex-1 flex flex-col">
        <WarrantyChatTour />
        <div className="flex items-center gap-2 mb-3">
          <button onClick={() => nav(-1)} data-testid="warranty-chat-back-link"
                  className="text-[11px] uppercase tracking-wider text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
            <ArrowLeft size={11} weight="bold" /> Back
          </button>
          <span className="text-[10px] uppercase tracking-wider text-slate-400 ml-auto">
            Status: <span className={closed ? "text-rose-600" : "text-emerald-700"}>{chat.claim_status || "—"}</span>
          </span>
        </div>

        {/* Action bar — staff only */}
        {isStaff && !closed && (
          <div className="bg-white border border-slate-200 rounded-md p-2 mb-3 flex flex-wrap items-center gap-2"
               data-testid="warranty-chat-actions">
            <button onClick={askAi} disabled={aiBusy}
                    data-testid="warranty-chat-ai-suggest"
                    className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border border-violet-300 text-violet-700 hover:bg-violet-50 disabled:opacity-50">
              <Robot size={11} weight="fill" />
              {aiBusy ? "AI thinking…" : "Ask AI: should we close?"}
            </button>
            {chat.can_close && (
              <button onClick={closeCase}
                      data-testid="warranty-chat-close-btn"
                      className="ml-auto inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-emerald-600 text-white hover:bg-emerald-700">
                <Lock size={11} weight="fill" /> Close case
              </button>
            )}
          </div>
        )}

        {/* Messages */}
        <ul className="flex-1 space-y-2 overflow-y-auto bg-white border border-slate-200 rounded-md p-3 min-h-[400px] max-h-[calc(100vh-280px)]"
            data-testid="warranty-chat-messages">
          {(chat.messages || []).length === 0 && (
            <li className="text-center text-xs italic text-slate-400 py-8">No messages yet — say hi to your tech.</li>
          )}
          {(chat.messages || []).map((m) => <MessageBubble key={m.msg_id} msg={m} userEmail={(user?.email || "").toLowerCase()} />)}
          <li ref={bottomRef} />
        </ul>

        {/* Composer */}
        {closed ? (
          <div className="mt-3 bg-rose-50 border border-rose-200 rounded-md p-3 text-xs text-rose-900"
               data-testid="warranty-chat-closed-banner">
            <Lock size={12} weight="fill" className="inline mr-1" />
            This case is closed.{chat.closed_by ? ` Closed by ${chat.closed_by}.` : ""}
          </div>
        ) : (
          <div className="mt-3 bg-white border border-slate-200 rounded-md p-2 flex items-end gap-2">
            <button onClick={() => fileInput.current?.click()} disabled={sending}
                    data-testid="warranty-chat-attach-btn"
                    title="Attach a photo, video, or file"
                    className="px-2 py-2 text-slate-500 hover:text-slate-800 disabled:opacity-50">
              <Paperclip size={16} weight="bold" />
            </button>
            <input ref={fileInput} type="file" className="hidden"
                   data-testid="warranty-chat-file-input"
                   onChange={(e) => uploadFile(e.target.files?.[0])} />
            <textarea
              value={composing}
              onChange={(e) => setComposing(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !sending) { e.preventDefault(); send(); } }}
              placeholder="Type a message — Enter to send, Shift+Enter for newline"
              rows={2}
              data-testid="warranty-chat-composer"
              className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-sm focus:border-blue-500 focus:outline-none resize-none"
            />
            <button onClick={send} disabled={sending || !composing.trim()}
                    data-testid="warranty-chat-send"
                    className="px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 inline-flex items-center gap-1.5">
              <PaperPlaneTilt size={11} weight="fill" />
              {sending ? "…" : "Send"}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

function MessageBubble({ msg, userEmail }) {
  const own = msg.author_email === userEmail;
  const isSystem = msg.kind === "system";
  const isAi = msg.kind === "ai";
  const isFile = msg.kind === "file";
  const t = msg.created_at ? new Date(msg.created_at).toLocaleString() : "";
  const turnIntoRule = () => {
    // iter144b — stash a one-shot prefill blob and jump to the rules page.
    // The page reads `__warranty_rule_prefill` from sessionStorage on mount
    // and clears it so a refresh doesn't re-trigger the modal.
    try {
      window.sessionStorage.setItem(
        "__warranty_rule_prefill",
        JSON.stringify({
          ai_instructions: (msg.body || "").slice(0, 4000),
          source: { kind: "chat_message", msg_id: msg.msg_id, author_email: msg.author_email },
        }),
      );
    } catch { /* sessionStorage unavailable — no-op */ }
    window.location.assign("/admin/warranty/evidence-rules?from_chat=1");
  };
  if (isSystem) {
    return (
      <li className="text-center" data-testid={`warranty-chat-msg-system-${msg.msg_id}`}>
        <span className="inline-block bg-slate-100 text-slate-700 text-[11px] italic px-3 py-1 rounded-sm whitespace-pre-line">
          {msg.body}
        </span>
      </li>
    );
  }
  if (isAi) {
    return (
      <li className="flex justify-start" data-testid={`warranty-chat-msg-ai-${msg.msg_id}`}>
        <div className="bg-violet-50 border-l-4 border-violet-500 p-3 rounded-r-md max-w-[90%] text-xs whitespace-pre-line">
          <div className="flex items-center gap-1.5 mb-1 text-[10px] uppercase tracking-wider text-violet-800 font-bold">
            <Sparkle size={10} weight="fill" /> AI assistant · {t}
          </div>
          <p className="text-slate-800">{msg.body}</p>
        </div>
      </li>
    );
  }
  return (
    <li className={`flex group ${own ? "justify-end" : "justify-start"}`} data-testid={`warranty-chat-msg-${msg.msg_id}`}>
      <div className={`relative max-w-[80%] px-3 py-2 rounded-md text-sm ${own ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-900"}`}>
        <div className={`text-[10px] mb-0.5 ${own ? "text-blue-100" : "text-slate-500"}`}>
          {msg.author_email || "?"} · {t}
        </div>
        <p className="whitespace-pre-wrap break-words">{msg.body}</p>
        {isFile && (msg.attachments || []).map((a) => (
          <div key={a.media_id} className={`mt-1 text-[11px] ${own ? "text-blue-100" : "text-blue-700"}`}>
            <Paperclip size={10} weight="bold" className="inline mr-1" />
            <span className="font-mono">{a.filename}</span>
            {a.size ? ` · ${Math.round(a.size / 1024)} KB` : null}
          </div>
        ))}
        {!own && (msg.body || "").trim().length >= 8 && (
          <button
            data-testid={`warranty-chat-make-rule-${msg.msg_id}`}
            title="Turn this message into an evidence rule"
            onClick={turnIntoRule}
            className="absolute -top-2 -right-2 hidden group-hover:flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider rounded-sm bg-amber-500 text-white shadow-sm hover:bg-amber-600">
            <Sparkle size={9} weight="fill" /> Make rule
          </button>
        )}
      </div>
    </li>
  );
}
